import * as R from "ramda";

import * as rangeInlineJSON from "../Functions/RangeInlineJSON";

import {
  nodeBlockInFlowContentKeys,
  nodeInlineInFlowContentKeys,
  nodeInlineNotFlowContentKeys,
} from "../Functions/base";

function patientInformationBlockChangeHighlight(
  node: DocBlockNode<unknown> | undefined
): BlockChangeHighlight | undefined {
  if (node?.type !== "patientInformation") {
    return undefined;
  }
  return (node as DocPatientInformationType<unknown> & { blockChangeHighlight?: BlockChangeHighlight })
    .blockChangeHighlight;
}

export function isActivePatientInformationNode(
  node: DocBlockNode<unknown> | undefined
): node is DocBlockNode<unknown> {
  return (
    node?.type === "patientInformation" &&
    patientInformationBlockChangeHighlight(node)?.type !== "DELETE_CONTENT"
  );
}

export function activePatientInformationCountInSection(
  parent: DocBlockNode<unknown>
): number {
  if (parent.type !== "section" || !("content" in parent)) {
    return 0;
  }

  return (
    (parent.content as Array<DocBlockNode<unknown> | undefined> | undefined)?.filter(
      isActivePatientInformationNode
    ).length ?? 0
  );
}

export function isPatientInformationPageSection(
  section: DocBlockNode<unknown>
): boolean {
  return (
    section.type === "section" &&
    (section.subtype === "patient_information_page" ||
      (section.classes?.includes("patient_information_card") ?? false))
  );
}

export function isOnlyPatientInformationOnPatientInformationPage(
  node: DocBlockNode<unknown>,
  parent: DocBlockNode<unknown> | undefined
): boolean {
  if (node.type !== "patientInformation" || !parent || parent.type !== "section") {
    return false;
  }

  if (!isPatientInformationPageSection(parent)) {
    return false;
  }

  return activePatientInformationCountInSection(parent) <= 1;
}

export function isDeletableNode(node: DocBlockNode<unknown>, contentKey: ContentKey) {
  switch (node.type) {
    case "paragraph":
    case "listItem":
    case "multipleChoiceAnswer":
    case "labValuesTable":
    case "labValuesTableRow":
    case "patientInformation":
      return true;
    default:
      return !contentKey;
  }
}

export function blockNodeFlowContentKey(node: DocBlockNode<unknown>) {
  switch (node.type) {
    case "paragraph":
    case "listItem":
    case "multipleChoiceAnswer":
    case "matchItem":
      return "content";
    default:
      return null;
  }
}

export function afterReturnNodeFor(nodeDefinitions: NodeDefinitions, node: DocBlockNode<unknown>) {
  switch (node.type) {
    case "paragraph":
    case "listItem":
    case "multipleChoiceAnswer":
    case "matchItem":
      const newNode = nodeDefinitions[node.type].generate(nodeDefinitions, {});

      return newNode;
    default:
      return null;
  }
}

export function addTextToBlockNodeFlow(node, text: string) {
  const nodeFlowKey = blockNodeFlowContentKey(node);
  if (nodeFlowKey) {
    node = R.assoc(nodeFlowKey, rangeInlineJSON.rangeInlineJSONWithText(text), node);
  }

  return node;
}

export function genrateNodesForAfterReturnNodeWithTextLines(nodeDefinitions: NodeDefinitions, node: DocBlockNode<unknown>, lines: string[]) {
  return lines.map((line) => {
    const newNode = afterReturnNodeFor(nodeDefinitions, node);
    return addTextToBlockNodeFlow(newNode, line);
  });
}

export function isFlowBlock(node: DocBlockNode<unknown>) {
  return nodeBlockInFlowContentKeys(node).length > 0;
}

export function isFlowInline(node: DocBlockNode<unknown>) {
  return nodeInlineInFlowContentKeys(node).length > 0;
}

export function isIsolatedInline(node: DocBlockNode<unknown>) {
  return nodeInlineNotFlowContentKeys(node).length > 0;
}
